import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import {
  TODAY,
  TRIPS,
  RAW_RESERVATIONS,
  PAY_STYLE,
  BOARD_STYLE,
  CLS_STYLE,
  ReservationItem,
  BoardStatus,
  tripById,
  remainingSeats,
  hydrateReservation,
  generateTicketCode,
  money,
  norm,
  fDate,
  fShort,
} from "./reservation-types";
import {
  ReservationWizardModal,
  BordereauModal,
  TicketModal,
  PaymentLinkModal,
  ModifRefModal,
  ReservationFullscreen,
  WizState,
  DzAction,
  newWiz,
  wizFor,
  applyWizard,
  nowStamp,
  nowIso,
} from "./reservation-modals";

const AGENT = "yasmine";

type Filters = {
  paiement: string;
  confirmation: string;
  source: string;
  depart: string;
  arrivee: string;
  createdBy: string;
  cls: string;
  canal: string;
  board: string;
  agence: string;
  from: string;
  to: string;
  q: string;
};

const EMPTY_F: Filters = { paiement: "Tous", confirmation: "Tous", source: "Tous", depart: "Tous", arrivee: "Tous", createdBy: "Tous", cls: "Tous", canal: "Tous", board: "Tous", agence: "Tous", from: "", to: "", q: "" };

const FILTER_LABELS: Record<string, string> = {
  paiement: "Paiement",
  confirmation: "Confirmation",
  source: "Source",
  depart: "Départ",
  arrivee: "Arrivée",
  createdBy: "Créé par",
  cls: "Classe",
  canal: "Canal",
  board: "Convoyage",
  agence: "Agence",
};

type SortKey = "created" | "ticket" | "name" | "trajet" | "pay" | "ref" | "montant" | "date" | "confirm" | "board";
type Cols = { ticket: boolean; refs: boolean; montant: boolean; board: boolean; audit: boolean };

const VIEW_STORAGE_KEY = "reservations.savedView";
const BOARD_ORDER: BoardStatus[] = ["En attente", "Vu", "Embarqué", "Absent", "Annulé"];

const LEGEND = [
  { color: "#22c55e", label: "Payé" },
  { color: "#f59e0b", label: "Partiel / solde dû" },
  { color: "#ef4444", label: "Non payé" },
  { color: "#7c3aed", label: "Confirmé par la réservation" },
  { color: "#fb923c", label: "En attente du convoyage" },
  { color: "#10b981", label: "Embarqué" },
  { color: "#94a3b8", label: "Annulé / remboursé" },
];

function loadSavedView(): { f: Filters; cols: Cols } | null {
  try {
    const raw = localStorage.getItem(VIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export interface ReservationsStore {
  list: ReservationItem[];
  setList: React.Dispatch<React.SetStateAction<ReservationItem[]>>;
  seq: number;
  setSeq: (n: number) => void;
}

// Shared by the console and the search engine so bookings made from either show up in both.
export function useReservationsStore(): ReservationsStore {
  const [list, setList] = useState<ReservationItem[]>(RAW_RESERVATIONS);
  const [seq, setSeq] = useState(6);
  return { list, setList, seq, setSeq };
}

export default function ReservationsConsole({ store }: { store: ReservationsStore }) {
  const saved = useMemo(loadSavedView, []);
  const { list, setList, seq, setSeq } = store;
  const [view, setView] = useState<"table" | "kanban" | "grid">("table");
  const [sortKey, setSortKey] = useState<SortKey>("created");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [menu, setMenu] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [panel, setPanel] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [cols, setCols] = useState<Cols>(saved?.cols ?? { ticket: true, refs: true, montant: true, board: true, audit: false });
  const [f, setFState] = useState<Filters>(saved?.f ?? EMPTY_F);
  const [wiz, setWiz] = useState<WizState | null>(null);
  const [bordOpen, setBordOpen] = useState(false);
  const [tk, setTk] = useState<string | null>(null);
  const [payL, setPayL] = useState<string[] | null>(null);
  const [refM, setRefM] = useState<string | null>(null);
  const [fsList, setFsList] = useState(false);
  const [fsResa, setFsResa] = useState<string | null>(null);
  const [pageFs, setPageFs] = useState(false);
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  const mob = width < 760;

  const flash = (msg: string) => {
    setToast(msg);
    setMenu(null);
    window.clearTimeout((flash as any)._t);
    (flash as any)._t = window.setTimeout(() => setToast(null), 2400);
  };
  const setF = (k: keyof Filters, v: string) => setFState((s) => ({ ...s, [k]: v }));

  const upd = (id: string, patch: Partial<ReservationItem>, ev?: string) => {
    const st = nowStamp();
    setList((prev) => prev.map((r) => (r.id === id ? hydrateReservation({ ...r, ...patch, audit: ev ? [...r.audit, [st, ev]] : r.audit }) : r)));
  };

  // Window size, native fullscreen state and Escape to leave the full-screen views.
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    const onFs = () => setPageFs(!!document.fullscreenElement);
    window.addEventListener("resize", onResize);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || tk || wiz || bordOpen || payL || refM) return;
      if (fsResa) setFsResa(null);
      else if (fsList) setFsList(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tk, wiz, bordOpen, payL, refM, fsResa, fsList]);

  const togglePageFs = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      return;
    }
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => flash("Plein écran bloqué par le navigateur — utilisez F11"));
    else flash("Plein écran non disponible — utilisez F11");
  };

  const openWiz = (w: WizState) => {
    setWiz(w);
    setMenu(null);
  };

  const quickConfirm = (r: ReservationItem) => {
    if (r.pay === "Payé" && r.trip && r.seat && !r.confirmed) {
      const code = generateTicketCode();
      upd(r.id, { confirmed: true, ticket: code, confirmedBy: AGENT, confirmedAt: nowIso() }, `Paiement validé, billet émis par ${AGENT} · siège ${r.seat}`);
      flash(`Billet émis — ${code}`);
    } else openWiz(wizFor(r));
  };

  /* ------------------------------ Filter / sort ----------------------------- */

  const base = list.filter((r) => {
    if (f.paiement !== "Tous" && r.pay !== f.paiement) return false;
    if (f.confirmation === "Confirmé" && !r.confirmed) return false;
    if (f.confirmation === "À confirmer" && r.confirmed) return false;
    if (f.source !== "Tous" && r.source !== f.source) return false;
    if (f.depart !== "Tous" && r.from !== f.depart) return false;
    if (f.arrivee !== "Tous" && r.to !== f.arrivee) return false;
    if (f.createdBy !== "Tous" && r.createdBy !== f.createdBy) return false;
    if (f.cls !== "Tous" && r.cls !== f.cls) return false;
    if (f.canal !== "Tous" && r.canal !== f.canal) return false;
    if (f.board !== "Tous" && r.board !== f.board) return false;
    if (f.agence !== "Tous" && r.agence !== f.agence) return false;
    if (f.from && (!r.date || r.date < f.from)) return false;
    if (f.to && (!r.date || r.date > f.to)) return false;
    const q = norm(f.q.trim());
    if (q) {
      const t = tripById(r.trip);
      if (!norm([r.pre, r.ticket, r.name, r.phone, r.cni, r.refOM, r.refMomo, r.from, r.to, t ? t.bus : ""].join(" ")).includes(q)) return false;
    }
    return true;
  });

  const sortVal = (r: ReservationItem): string | number =>
    ({
      created: r.createdAt,
      ticket: r.ticket || "",
      name: r.name,
      trajet: r.from + r.to,
      pay: r.pay,
      ref: r.refOM + r.refMomo,
      montant: r.montant,
      date: (r.date || "9999") + r.heure,
      confirm: r.confirmed ? 1 : 0,
      board: BOARD_ORDER.indexOf(r.board),
    }[sortKey]);
  const rows = base.slice().sort((a, b) => {
    const x = sortVal(a);
    const y = sortVal(b);
    const dir = sortDir === "asc" ? 1 : -1;
    return typeof x === "number" && typeof y === "number" ? (x - y) * dir : String(x).localeCompare(String(y), "fr") * dir;
  });
  const sortBy = (k: SortKey) => {
    setSortDir(sortKey === k && sortDir === "asc" ? "desc" : "asc");
    setSortKey(k);
  };

  const uniq = (k: keyof ReservationItem) => ["Tous", ...Array.from(new Set(list.map((r) => String(r[k])))).filter((v) => v && v !== "—")];
  const pills = [
    ...Object.keys(FILTER_LABELS)
      .filter((k) => f[k as keyof Filters] !== "Tous")
      .map((k) => ({ label: `${FILTER_LABELS[k]} : ${f[k as keyof Filters]}`, clear: () => setF(k as keyof Filters, "Tous") })),
    ...(f.from ? [{ label: `Du ${f.from}`, clear: () => setF("from", "") }] : []),
    ...(f.to ? [{ label: `Au ${f.to}`, clear: () => setF("to", "") }] : []),
  ];

  const live = base.filter((r) => r.pay !== "Remboursé");
  const paid = live.reduce((a, r) => a + r.paid, 0);
  const total = live.reduce((a, r) => a + r.montant, 0);
  const unpaid = base.filter((r) => r.pay === "Non payé" && r.board !== "Annulé");
  const toConfirm = base.filter((r) => !r.confirmed && (r.pay === "Payé" || r.pay === "Partiel"));
  const todayTrips = TRIPS.filter((t) => t.date === TODAY);
  const todayRem = todayTrips.reduce((a, t) => a + remainingSeats(t, list), 0);

  const selIds = Object.keys(sel).filter((k) => sel[k] && list.some((r) => r.id === k));
  const selRows = list.filter((r) => selIds.includes(r.id));

  /* ------------------------------ Row decoration ---------------------------- */

  const isDup = (r: ReservationItem) => r.pay === "Non payé" && list.filter((x) => x.phone === r.phone && x.trip === r.trip && x.pay === "Non payé").length > 1;

  const rowActions = (r: ReservationItem) => {
    const refunded = r.pay === "Remboursé";
    const A = (label: string, dot: string, run: () => void, color = "#3f3a52") => ({ label, dot, color, run: () => { setMenu(null); run(); } });
    return [
      A("Ouvrir en plein écran", "#6d28d9", () => setFsResa(r.id)),
      A("Modifier", "#7c3aed", () => openWiz(wizFor(r, 2))),
      !r.confirmed && !refunded && A("Confirmer", "#22c55e", () => quickConfirm(r), "#15803d"),
      !r.confirmed && !refunded && A("Générer lien de paiement", "#eab308", () => setPayL([r.id])),
      A("Modifier référence paiement", "#0891b2", () => setRefM(r.id)),
      !!r.ticket && A("Voir / imprimer le ticket", "#6366f1", () => setTk(r.id)),
      A("Changer de voyage / siège", "#0ea5e9", () => openWiz(wizFor(r, 1))),
      !!r.ticket && A("Envoyer le billet par WhatsApp", "#16a34a", () => flash(`Billet ${r.ticket} envoyé à ${r.phone}`)),
      r.confirmed && r.board !== "Embarqué" && A("Marquer embarqué", "#10b981", () => upd(r.id, { board: "Embarqué" }, `Embarquement validé par ${AGENT}`)),
      r.confirmed && r.board !== "Absent" && A("Marquer absent", "#f59e0b", () => upd(r.id, { board: "Absent" }, "Passager absent au départ")),
      !refunded &&
        A(
          "Annuler / rembourser",
          "#f59e0b",
          () => {
            upd(r.id, { pay: r.paid > 0 ? "Remboursé" : r.pay, paid: 0, board: "Annulé" }, r.paid > 0 ? `Annulé · remboursement ${money(r.paid)}` : "Réservation annulée");
            flash("Réservation annulée");
          },
          "#b45309"
        ),
      A(
        "Supprimer",
        "#ef4444",
        () => {
          setList((prev) => prev.filter((x) => x.id !== r.id));
          if (fsResa === r.id) setFsResa(null);
          flash("Réservation supprimée");
        },
        "#dc2626"
      ),
    ].filter(Boolean) as { label: string; dot: string; color: string; run: () => void }[];
  };

  const dateSub = (r: ReservationItem) => {
    const due = r.montant - r.paid;
    if (r.pay === "Remboursé") return "Remboursé";
    if (r.confirmed) return "Billet émis";
    if (r.pay === "Non payé") return r.linkSent ? "Lien de paiement envoyé" : "En attente de paiement";
    if (r.pay === "Partiel") return `Solde dû ${money(due)}`;
    return "Paiement à confirmer";
  };
  const rowBg = (r: ReservationItem) => (r.pay === "Non payé" ? "#fffafa" : r.confirmed ? "#ffffff" : "#fffdf7");
  const canConfirm = (r: ReservationItem) => !r.confirmed && r.pay !== "Remboursé" && r.board !== "Annulé";

  const ActionMenu = ({ r, className }: { r: ReservationItem; className: string }) => (
    <>
      <div className="fixed inset-0 z-10" onClick={() => setMenu(null)} />
      <div className={classNames("absolute z-20 w-[250px] p-1.5 bg-white border border-[#e6e0f5] rounded-xl shadow-[0_18px_40px_rgba(31,27,46,0.16)] text-left", className)}>
        <div className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold tracking-[0.06em] uppercase text-[#8b81a8]">Actions</div>
        {rowActions(r).map((a) => (
          <button key={a.label} type="button" onClick={a.run} className="flex items-center gap-2 w-full px-2.5 py-2 rounded-lg text-[12.5px] font-medium text-left whitespace-nowrap hover:bg-[#f6f3fd]" style={{ color: a.color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: a.dot }} />
            {a.label}
          </button>
        ))}
      </div>
    </>
  );

  const Chip = ({ text, bg, fg, mono, className }: { text: React.ReactNode; bg: string; fg: string; mono?: boolean; className?: string }) => (
    <span className={classNames("inline-block whitespace-nowrap", mono && "font-mono", className)} style={{ background: bg, color: fg }}>
      {text}
    </span>
  );

  const Route = ({ r, size = "sm" }: { r: ReservationItem; size?: "sm" | "xs" }) => (
    <div className="flex items-center gap-1.5">
      <span className={classNames("bg-[#ede9fe] rounded-md font-bold text-[#5b21b6] whitespace-nowrap", size === "sm" ? "px-2 py-1 text-[11.5px]" : "px-2 py-0.5 text-[11px]")}>{r.from || "—"}</span>
      <span className="text-[#b6afc9]">→</span>
      <span className={classNames("bg-[#ffedd5] rounded-md font-bold text-[#c2410c] whitespace-nowrap", size === "sm" ? "px-2 py-1 text-[11.5px]" : "px-2 py-0.5 text-[11px]")}>{r.to || "—"}</span>
    </div>
  );

  const PreChip = ({ r, className }: { r: ReservationItem; className: string }) => <Chip text={r.pre || "—"} bg={r.pre ? "#dcfce7" : "#f4f4f5"} fg={r.pre ? "#166534" : "#a1a1aa"} mono className={className} />;
  const PayChip = ({ r, className }: { r: ReservationItem; className: string }) => <Chip text={r.pay} bg={PAY_STYLE[r.pay].bg} fg={PAY_STYLE[r.pay].fg} className={classNames("font-bold", className)} />;

  const ConfirmBtn = ({ r, className }: { r: ReservationItem; className: string }) =>
    canConfirm(r) ? (
      <button type="button" onClick={() => quickConfirm(r)} className={classNames("bg-[#dcfce7] text-[#15803d] font-bold whitespace-nowrap hover:bg-[#bbf7d0]", className)}>
        Confirmer
      </button>
    ) : null;

  /* ---------------------------------- Views --------------------------------- */

  const headers: { label: string; k: SortKey; on: boolean }[] = [
    { label: "Code pré-réservation / créé par", k: "created", on: true },
    { label: "Code ticket / confirmé par", k: "ticket", on: cols.ticket },
    { label: "Client", k: "name", on: true },
    { label: "Trajet", k: "trajet", on: true },
    { label: "Statut paiement", k: "pay", on: true },
    { label: "Réf. paiement", k: "ref", on: cols.refs },
    { label: "Montant", k: "montant", on: cols.montant },
    { label: "Date de voyage", k: "date", on: true },
    { label: "Confirmer", k: "confirm", on: true },
    { label: "Convoyage", k: "board", on: cols.board },
    { label: "Historique", k: "created", on: cols.audit },
  ];

  const tableView = (
    <div className="bg-white border border-[#ece7f8] rounded-[14px] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1560px] border-collapse text-[13px]">
          <thead>
            <tr className="bg-[#f6f3fd]">
              <th className="w-[42px] py-3 pl-4 text-left">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && rows.every((r) => sel[r.id])}
                  onChange={() => {
                    const all = rows.every((r) => sel[r.id]);
                    const next: Record<string, boolean> = {};
                    rows.forEach((r) => (next[r.id] = !all));
                    setSel(next);
                  }}
                  className="w-[15px] h-[15px] accent-[#6d28d9]"
                />
              </th>
              {headers
                .filter((h) => h.on)
                .map((h) => (
                  <th
                    key={h.label}
                    onClick={() => sortBy(h.k)}
                    className="px-3.5 py-3 text-left text-[10.5px] font-bold tracking-[0.07em] uppercase text-[#6b6480] whitespace-nowrap cursor-pointer select-none border-b border-[#ece7f8]"
                  >
                    {h.label}
                    <span className="text-[#6d28d9] ml-1">{sortKey === h.k && h.label !== "Historique" ? (sortDir === "asc" ? "▲" : "▼") : ""}</span>
                  </th>
                ))}
              <th className="py-3 pr-4 pl-2 border-b border-[#ece7f8]" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const t = tripById(r.trip);
              const c = CLS_STYLE[r.cls] || CLS_STYLE.CLASSIQUE;
              const b = BOARD_STYLE[r.board];
              const due = r.montant - r.paid;
              const refunded = r.pay === "Remboursé";
              return (
                <tr key={r.id} className="border-b border-[#f4f1fb] align-top" style={{ background: sel[r.id] ? "#f7f3ff" : rowBg(r) }}>
                  <td className="py-3.5 pl-4">
                    <input type="checkbox" checked={!!sel[r.id]} onChange={() => setSel((s) => ({ ...s, [r.id]: !s[r.id] }))} className="w-[15px] h-[15px] accent-[#6d28d9]" />
                  </td>
                  <td className="px-3.5 py-3">
                    <PreChip r={r} className="px-2 py-1 rounded-[7px] text-xs font-medium" />
                    <div className="text-[11.5px] text-[#6b6480] mt-1.5 leading-snug">
                      {r.createdBy}, {fDate(r.createdAt)}
                    </div>
                    <div className="text-[11px] text-[#9b93b3]">
                      Source : {r.source}
                      {r.agence ? ` · ${r.agence}` : ""}
                    </div>
                  </td>
                  {cols.ticket && (
                    <td className="px-3.5 py-3">
                      <Chip text={r.ticket || "à émettre"} bg={r.ticket ? "#ede9fe" : "#f4f4f5"} fg={r.ticket ? "#5b21b6" : "#a1a1aa"} mono className="px-2 py-1 rounded-[7px] text-xs" />
                      <div className="text-[11.5px] text-[#6b6480] mt-1.5 leading-snug">{r.ticket ? `${r.confirmedBy}, ${fDate(r.confirmedAt)}` : "aucun billet émis"}</div>
                    </td>
                  )}
                  <td className="px-3.5 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[13px] font-bold text-[#241f36] leading-snug">{r.name}</span>
                      {isDup(r) && (
                        <span title="Même téléphone, même voyage, non payé" className="px-1.5 py-px bg-[#fef3c7] rounded-[5px] text-[10px] font-bold text-[#92400e] whitespace-nowrap">
                          Doublon ?
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[11.5px] text-[#6b6480] mt-0.5">{r.phone}</div>
                    <div className="text-[11px] text-[#9b93b3] mt-0.5">CNI {r.cni || "-"}</div>
                  </td>
                  <td className="px-3.5 py-3">
                    <Route r={r} />
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Chip text={r.cls} bg={c.bg} fg={c.fg} className="px-1.5 py-0.5 rounded-[5px] text-[10.5px] font-bold" />
                      <span className="text-[11.5px] text-[#6b6480]">{t ? t.bus : "Bus non affecté"}</span>
                    </div>
                    <div className="text-[11px] text-[#9b93b3] mt-0.5">{r.seat ? `Siège ${r.seat}` : t ? "Siège non attribué" : ""}</div>
                  </td>
                  <td className="px-3.5 py-3">
                    <PayChip r={r} className="px-2.5 py-1 rounded-[7px] text-xs" />
                    <div className="text-[11px] text-[#9b93b3] mt-1.5">{r.canal}</div>
                  </td>
                  {cols.refs && (
                    <td className="px-3.5 py-3">
                      <div className="grid gap-1 text-[11px] text-[#6b6480]">
                        <div>
                          <span className="text-[#f97316] font-bold">OM</span> <span className="font-mono">{r.refOM || "–"}</span>
                        </div>
                        <div>
                          <span className="text-[#ca8a04] font-bold">MoMo</span> <span className="font-mono">{r.refMomo || "–"}</span>
                        </div>
                      </div>
                    </td>
                  )}
                  {cols.montant && (
                    <td className="px-3.5 py-3 text-right whitespace-nowrap">
                      <div className="text-[13px] font-bold text-[#241f36]">{money(r.montant)}</div>
                      <div className="text-[11px] mt-1" style={{ color: refunded ? "#64748b" : due > 0 ? "#b91c1c" : "#16a34a" }}>
                        {refunded ? "remboursé" : due > 0 ? `reste ${money(due)}` : "encaissé"}
                      </div>
                    </td>
                  )}
                  <td className="px-3.5 py-3 whitespace-nowrap">
                    <div className="text-[12.5px] font-semibold text-[#241f36]">{r.date || "—"}</div>
                    <div className="text-[11.5px] text-[#6b6480] mt-0.5">{r.heure || "heure à définir"}</div>
                    <div className="text-[11px] font-semibold mt-1" style={{ color: refunded ? "#64748b" : r.confirmed ? "#16a34a" : "#c2410c" }}>
                      {dateSub(r)}
                    </div>
                  </td>
                  <td className="px-3.5 py-3">
                    {r.confirmed && <span className="inline-block px-2.5 py-1 bg-[#ede9fe] rounded-[7px] text-xs font-bold text-[#5b21b6]">Confirmé</span>}
                    <ConfirmBtn r={r} className="px-3 py-1.5 border border-[#bbf7d0] rounded-lg text-xs" />
                    <div className="text-[11px] text-[#9b93b3] mt-1.5">{r.confirmed ? `par ${r.confirmedBy}` : refunded ? "dossier clos" : "paiement non validé"}</div>
                  </td>
                  {cols.board && (
                    <td className="px-3.5 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[7px] text-[11.5px] font-bold whitespace-nowrap" style={{ background: b.bg, color: b.fg }}>
                        <span className="w-[7px] h-[7px] rounded-full" style={{ background: b.dot }} />
                        {r.board}
                      </span>
                      <div className="text-[11px] text-[#9b93b3] mt-1.5">{t ? t.bus : "Non affecté"}</div>
                    </td>
                  )}
                  {cols.audit && (
                    <td className="px-3.5 py-3 max-w-[220px]">
                      <div className="text-[11.5px] text-[#6b6480] leading-normal">
                        {r.audit.map((a) => `${a[0]} ${a[1]}`).join(" · ")}
                        {r.note ? ` · ${r.note}` : ""}
                      </div>
                    </td>
                  )}
                  <td className="py-3 pr-4 pl-2 relative text-right whitespace-nowrap">
                    <button type="button" onClick={() => setFsResa(r.id)} title="Observer cette réservation en plein écran" className="w-[30px] h-[30px] mr-1.5 border border-[#ece7f8] bg-white rounded-lg text-[13px] text-[#6d28d9] hover:bg-[#f6f3fd]">
                      ⛶
                    </button>
                    <button type="button" onClick={() => setMenu(menu === r.id ? null : r.id)} className="w-[30px] h-[30px] border border-[#ece7f8] bg-white rounded-lg text-[15px] text-[#6b6480] hover:bg-[#f6f3fd]">
                      ⋯
                    </button>
                    {menu === r.id && <ActionMenu r={r} className="right-4 top-11" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div className="p-8 text-center text-[13px] text-[#9b93b3]">Aucune réservation ne correspond à ces filtres.</div>}
      <div className="flex items-center justify-between gap-4 flex-wrap px-[18px] py-3 bg-[#fbf9ff] border-t border-[#ece7f8] text-[12.5px] text-[#6b6480]">
        <div>
          {rows.length} lignes affichées · tri {sortDir === "asc" ? "croissant" : "décroissant"}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <span>
            Encaissé <strong className="text-[#166534]">{money(paid)}</strong>
          </span>
          <span>
            Reste à encaisser <strong className="text-[#b91c1c]">{money(Math.max(0, total - paid))}</strong>
          </span>
        </div>
      </div>
    </div>
  );

  const lanes = [
    { title: "Non payé", dot: "#ef4444", pred: (r: ReservationItem) => r.pay === "Non payé" && r.board !== "Annulé" },
    { title: "Payé — à confirmer", dot: "#f59e0b", pred: (r: ReservationItem) => (r.pay === "Payé" || r.pay === "Partiel") && !r.confirmed },
    { title: "Confirmé — billet émis", dot: "#7c3aed", pred: (r: ReservationItem) => r.confirmed && r.pay !== "Remboursé" && (r.board === "En attente" || r.board === "Vu") },
    { title: "Embarqué", dot: "#10b981", pred: (r: ReservationItem) => r.board === "Embarqué" },
    { title: "Annulé / absent", dot: "#94a3b8", pred: (r: ReservationItem) => r.pay === "Remboursé" || r.board === "Annulé" || r.board === "Absent" },
  ];

  const kanbanView = (
    <div className="grid grid-flow-col auto-cols-[minmax(280px,1fr)] gap-3.5 overflow-x-auto pb-2">
      {lanes.map((l) => {
        const items = rows.filter(l.pred);
        return (
          <div key={l.title} className="bg-white border border-[#ece7f8] rounded-[14px] p-3 min-w-[280px]">
            <div className="flex items-center gap-2 px-1 pt-0.5 pb-3">
              <span className="w-[9px] h-[9px] rounded-full" style={{ background: l.dot }} />
              <span className="text-[13px] font-bold text-[#241f36]">{l.title}</span>
              <span className="px-2 py-0.5 bg-[#f1ecfe] rounded-full text-[11.5px] font-bold text-[#5b21b6]">{items.length}</span>
              <span className="ml-auto text-[11.5px] text-[#8b81a8]">{money(items.reduce((a, r) => a + r.montant, 0))}</span>
            </div>
            <div className="grid gap-2.5">
              {items.map((r) => (
                <div key={r.id} className="relative p-3 border border-[#efeaf9] border-l-[3px] rounded-[10px]" style={{ borderLeftColor: l.dot, background: rowBg(r) }}>
                  <div className="flex justify-between gap-2 items-start">
                    <PreChip r={r} className="px-1.5 py-0.5 rounded-md text-[11px]" />
                    <PayChip r={r} className="px-2 py-0.5 rounded-md text-[11px]" />
                  </div>
                  <div className="text-[11px] text-[#9b93b3] mt-1.5">
                    {r.createdBy}, {fDate(r.createdAt)}
                  </div>
                  <div className="text-[13px] font-bold text-[#241f36] mt-1">{r.name}</div>
                  <div className="font-mono text-[11px] text-[#6b6480] mt-0.5">{r.phone}</div>
                  <div className="mt-2">
                    <Route r={r} size="xs" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2.5 pt-2 border-t border-[#f4f1fb]">
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.06em] text-[#a79ec9]">MONTANT</div>
                      <div className="text-xs text-[#241f36] mt-0.5">{money(r.montant)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.06em] text-[#a79ec9]">CODE TICKET</div>
                      <div className="font-mono text-[11.5px] text-[#241f36] mt-0.5">{r.ticket || "à émettre"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2.5">
                    <ConfirmBtn r={r} className="h-[30px] px-3 rounded-[7px] text-xs" />
                    <button type="button" onClick={() => flash(`WhatsApp envoyé à ${r.name} (${r.phone})`)} className="h-[30px] px-3 bg-[#f1ecfe] rounded-[7px] text-xs font-semibold text-[#6d28d9]">
                      Contacter
                    </button>
                    <button type="button" onClick={() => setFsResa(r.id)} title="Plein écran" className="ml-auto w-7 h-7 border border-[#ece7f8] bg-white rounded-[7px] text-xs text-[#6d28d9]">
                      ⛶
                    </button>
                    <button type="button" onClick={() => setMenu(menu === r.id ? null : r.id)} className="w-7 h-7 border border-[#ece7f8] bg-white rounded-[7px] text-sm text-[#6b6480]">
                      ⋯
                    </button>
                  </div>
                  {menu === r.id && <ActionMenu r={r} className="right-2 top-[calc(100%-6px)]" />}
                </div>
              ))}
              {items.length === 0 && <div className="p-[18px] border border-dashed border-[#e6e0f5] rounded-[10px] text-xs text-[#b6afc9] text-center">Aucune réservation</div>}
            </div>
          </div>
        );
      })}
    </div>
  );

  const gridView = (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(min(320px,100%),1fr))] gap-3.5">
      {rows.map((r) => {
        const c = CLS_STYLE[r.cls] || CLS_STYLE.CLASSIQUE;
        const b = BOARD_STYLE[r.board];
        const t = tripById(r.trip);
        return (
          <div key={r.id} className="relative p-4 bg-white border border-[#ece7f8] rounded-[14px]">
            <div className="flex justify-between items-start gap-2.5">
              <div>
                <PreChip r={r} className="px-2 py-0.5 rounded-md text-[11.5px]" />
                <div className="text-[11px] text-[#9b93b3] mt-1">
                  {r.createdBy}, {fDate(r.createdAt)}
                </div>
              </div>
              <PayChip r={r} className="px-2 py-1 rounded-[7px] text-[11.5px]" />
            </div>
            <div className="text-[15px] font-bold text-[#241f36] mt-3">{r.name}</div>
            <div className="font-mono text-[11.5px] text-[#6b6480] mt-0.5">{r.phone}</div>
            <div className="flex items-center gap-1.5 mt-3">
              <Route r={r} />
              <Chip text={r.cls} bg={c.bg} fg={c.fg} className="ml-auto px-1.5 py-0.5 rounded-[5px] text-[10.5px] font-bold" />
            </div>
            <div className="grid grid-cols-2 gap-2.5 mt-3 pt-3 border-t border-[#f4f1fb]">
              <GridCell label="Montant" value={money(r.montant)} />
              <GridCell label="Code ticket" value={r.ticket || "à émettre"} mono />
              <GridCell label="Départ" value={`${r.date || "—"} · ${r.heure || "heure à définir"}`} />
              <div>
                <div className="text-[10.5px] tracking-[0.06em] uppercase text-[#a79ec9] font-bold">Convoyage</div>
                <div className="text-[12.5px] font-semibold mt-0.5" style={{ color: b.fg }}>
                  {r.board} · {t ? t.bus : "Non affecté"}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-3.5">
              <ConfirmBtn r={r} className="flex-1 h-[34px] rounded-lg text-[12.5px]" />
              <button type="button" onClick={() => flash(`WhatsApp envoyé à ${r.name} (${r.phone})`)} className="h-[34px] px-3 bg-[#f1ecfe] rounded-lg text-[12.5px] font-semibold text-[#6d28d9]">
                Contacter
              </button>
              <button type="button" onClick={() => setFsResa(r.id)} title="Plein écran" className="w-[34px] h-[34px] border border-[#ece7f8] bg-white rounded-lg text-[13px] text-[#6d28d9]">
                ⛶
              </button>
              <button type="button" onClick={() => setMenu(menu === r.id ? null : r.id)} className="w-[34px] h-[34px] border border-[#ece7f8] bg-white rounded-lg text-[15px] text-[#6b6480]">
                ⋯
              </button>
            </div>
            {menu === r.id && <ActionMenu r={r} className="right-3 top-[calc(100%-8px)]" />}
          </div>
        );
      })}
      {rows.length === 0 && <div className="p-8 text-center text-[13px] text-[#9b93b3]">Aucune réservation ne correspond à ces filtres.</div>}
    </div>
  );

  /* --------------------------------- Modals --------------------------------- */

  const fsR = fsResa ? list.find((x) => x.id === fsResa) : undefined;
  const fsIds = rows.map((x) => x.id);
  const fsIdx = fsR ? fsIds.indexOf(fsR.id) : -1;
  const goFs = (k: number) => {
    if (!fsIds.length) return;
    const j = fsIdx < 0 ? 0 : (fsIdx + k + fsIds.length) % fsIds.length;
    setFsResa(fsIds[j]);
  };
  const fsActions: DzAction[] = fsR
    ? ([
        fsR.ticket && { label: "Imprimer le ticket", run: () => setTk(fsR.id), kind: "p" },
        canConfirm(fsR) && { label: "Confirmer", run: () => quickConfirm(fsR), kind: "g" },
        canConfirm(fsR) && { label: "Lien de paiement", run: () => setPayL([fsR.id]) },
        { label: "Modifier", run: () => openWiz(wizFor(fsR, 2)) },
        { label: "WhatsApp", run: () => flash(`WhatsApp envoyé à ${fsR.name}`) },
      ].filter(Boolean) as DzAction[])
    : [];

  const fabs = [
    { label: "Chat", glyph: "◻", title: "Messagerie équipe", bg: "#fee2e2", fg: "#dc2626", run: () => flash("Messagerie équipe ouverte") },
    { label: "Réserver", glyph: "+", title: "Nouvelle pré-réservation", bg: "#f97316", fg: "#fff", run: () => openWiz(newWiz("resa")) },
    { label: "Ticket", glyph: "+", title: "Vendre un billet au guichet", bg: "#3b82f6", fg: "#fff", run: () => openWiz(newWiz("ticket")) },
    { label: "Bordereau", glyph: "≡", title: "Bordereau des départs", bg: "#6d28d9", fg: "#fff", run: () => setBordOpen(true) },
    { label: "Enquête", glyph: "”", title: "Enquête de satisfaction", bg: "#64748b", fg: "#fff", run: () => flash("Enquête de satisfaction envoyée aux voyageurs d'hier") },
    { label: "Filtre Global", glyph: "▽", title: "Afficher / masquer les filtres", bg: "#22c55e", fg: "#fff", run: () => setFiltersOpen((v) => !v) },
  ];

  const bulkActions: { label: string; run: () => void }[] = [
    {
      label: "Confirmer les payées",
      run: () => {
        const ok = selRows.filter((r) => r.pay === "Payé" && r.trip && r.seat && !r.confirmed);
        ok.forEach((r) => upd(r.id, { confirmed: true, ticket: generateTicketCode(), confirmedBy: AGENT, confirmedAt: nowIso() }, "Billet émis (confirmation groupée)"));
        flash(`${ok.length} billet(s) émis · ${selRows.length - ok.length} ignorée(s)`);
      },
    },
    { label: "Générer lien de paiement", run: () => setPayL(selRows.filter((r) => r.pay !== "Payé").map((r) => r.id)) },
    { label: "Envoyer les billets WhatsApp", run: () => flash(`${selRows.filter((r) => r.ticket).length} billet(s) envoyé(s) par WhatsApp`) },
    {
      label: "Marquer embarqués",
      run: () => {
        selRows.filter((r) => r.confirmed).forEach((r) => upd(r.id, { board: "Embarqué" }, "Embarqué (action groupée)"));
        flash("Embarquement mis à jour");
      },
    },
    { label: "Imprimer les tickets", run: () => flash(`${selRows.filter((r) => r.ticket).length} ticket(s) envoyé(s) à l'imprimante`) },
    { label: "Exporter la sélection", run: () => flash(`Export de ${selRows.length} réservation(s)`) },
    {
      label: "Supprimer",
      run: () => {
        setList((prev) => prev.filter((r) => !selIds.includes(r.id)));
        flash(`${selRows.length} réservation(s) supprimée(s)`);
      },
    },
  ];

  const tabs = (
    <div className="flex p-[3px] bg-[#f1ecfe] rounded-[10px] gap-[3px]">
      {(
        [
          ["table", "Tableau"],
          ["kanban", "Kanban statut"],
          ["grid", "Grille"],
        ] as const
      ).map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => setView(k)}
          className={classNames("px-3.5 py-1.5 rounded-lg text-[13px] font-semibold whitespace-nowrap", view === k ? "bg-white text-[#4c1d95] shadow-[0_1px_3px_rgba(76,29,149,.16)]" : "text-[#7c729b]")}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const selectCls = "w-full h-9 px-2.5 border border-[#e2e8f0] rounded-lg bg-white text-[13px] text-[#1f1b2e]";
  const fieldLabelCls = "block text-[11px] font-bold tracking-[0.06em] text-[#8b81a8] uppercase mb-1";
  const filterFields: { label: string; key: keyof Filters; options: string[] }[] = [
    { label: "Statut paiement", key: "paiement", options: ["Tous", "Payé", "Partiel", "Non payé", "Remboursé"] },
    { label: "Confirmation", key: "confirmation", options: ["Tous", "Confirmé", "À confirmer"] },
    { label: "Source", key: "source", options: uniq("source") },
    { label: "Départ", key: "depart", options: uniq("from") },
    { label: "Arrivée", key: "arrivee", options: uniq("to") },
    { label: "Créé par", key: "createdBy", options: uniq("createdBy") },
    { label: "Classe", key: "cls", options: ["Tous", "CLASSIQUE", "VIP"] },
    { label: "Canal de paiement", key: "canal", options: ["Tous", "Orange Money", "MTN MoMo", "Espèces"] },
    { label: "Convoyage", key: "board", options: ["Tous", ...BOARD_ORDER] },
    { label: "Agence", key: "agence", options: uniq("agence") },
  ];

  return (
    <div className={classNames("min-h-full flex flex-col bg-[#faf7ff] text-[#1f1b2e] font-['Public_Sans',sans-serif] overflow-clip", mob ? "px-3.5 pt-4 pb-[150px]" : "pl-[26px] pr-[128px] pt-[22px] pb-[90px]")}>
      {/* Header — stays pinned under the app header while the page scrolls */}
      <div className={classNames("sticky top-[50px] z-30 bg-[#faf7ff] flex items-start justify-between gap-6 flex-wrap pb-3 mb-[18px]", mob ? "-mx-3.5 px-3.5 -mt-4 pt-4" : "-ml-[26px] pl-[26px] -mr-[128px] pr-[128px] -mt-[22px] pt-[22px]")}>
        <div>
          <div className="text-[22px] font-bold tracking-[-0.01em]">Les Réservations</div>
          <div className="text-[13px] text-[#6b6480] mt-1">
            {base.length} dossiers affichés sur {list.length} · {base.filter((r) => !r.confirmed && r.pay !== "Remboursé").length} à confirmer · {unpaid.length} non payés
          </div>
        </div>
        <div className="flex gap-2.5 items-center flex-wrap">
          <HeaderBtn onClick={() => flash(`Export Excel généré (${base.length} lignes, colonnes visibles)`)} dot="#16a34a" label="Export Excel" />
          <HeaderBtn onClick={() => flash(`Export PDF généré (${base.length} lignes)`)} dot="#dc2626" label="Export PDF" />
          <button type="button" onClick={() => flash("Synchronisation des réservations en ligne lancée")} className="h-[38px] px-3.5 border border-[#bfdbfe] bg-[#eff6ff] hover:bg-[#dbeafe] rounded-[9px] text-[13px] font-semibold text-[#1d4ed8] whitespace-nowrap">
            Sync. Réservations
          </button>
          <button type="button" onClick={togglePageFs} title="Afficher toute la page en plein écran" className="flex items-center gap-1.5 h-[38px] px-3.5 border border-[#1f1b2e] bg-[#1f1b2e] hover:bg-[#332c4a] rounded-[9px] text-[13px] font-semibold text-white whitespace-nowrap">
            <span className="text-sm leading-none">⛶</span>
            {pageFs ? "Quitter le plein écran" : "Plein écran"}
          </button>
          <button type="button" onClick={() => openWiz(newWiz("resa"))} className="h-[38px] px-4 bg-[#6d28d9] hover:bg-[#5b21b6] rounded-[9px] text-[13px] font-bold text-white whitespace-nowrap">
            + Créer Réservation
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3 mb-3.5">
        {[
          { label: "Non payées", value: String(unpaid.length), sub: `${unpaid.filter((r) => r.linkSent).length} avec lien de paiement envoyé`, color: "#b91c1c" },
          { label: "Paiements à confirmer", value: String(toConfirm.length), sub: "réf. reçue, billet non émis", color: "#b45309" },
          { label: "Billets émis", value: String(base.filter((r) => r.ticket && r.pay !== "Remboursé").length), sub: `${base.filter((r) => r.board === "Embarqué").length} embarqués`, color: "#5b21b6" },
          { label: "Places libres aujourd'hui", value: String(todayRem), sub: `${todayTrips.length} départs le ${fShort(TODAY)}`, color: "#0369a1" },
          { label: "Encaissé", value: money(paid), sub: `reste ${money(Math.max(0, total - paid))}`, color: "#047857" },
        ].map((k) => (
          <div key={k.label} className="px-[15px] py-[13px] bg-white border border-[#ece7f8] rounded-xl">
            <div className="text-[11px] font-bold tracking-[0.06em] uppercase text-[#8b81a8]">{k.label}</div>
            <div className="text-xl font-bold mt-1.5" style={{ color: k.color }}>
              {k.value}
            </div>
            <div className="text-[11.5px] text-[#9b93b3] mt-0.5">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Tabs, search, columns, filters */}
      <div className="flex items-center gap-3 flex-wrap mb-3">
        {tabs}
        <input
          value={f.q}
          onChange={(e) => setF("q", e.target.value)}
          placeholder="Code, ticket, client, téléphone, CNI, bus, réf. paiement…"
          className="flex-1 min-w-[260px] h-[38px] px-3.5 border border-[#ddd6fe] rounded-[9px] bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-purple-300"
        />
        <button type="button" onClick={() => setPanel(!panel)} className="h-[38px] px-3.5 border border-[#ddd6fe] bg-white hover:bg-[#f5f3ff] rounded-[9px] text-[13px] font-semibold text-[#4c1d95] whitespace-nowrap">
          Colonnes
        </button>
        <button type="button" onClick={() => setFiltersOpen(!filtersOpen)} className="h-[38px] px-3.5 border border-[#ddd6fe] bg-[#f5f3ff] hover:bg-[#ede9fe] rounded-[9px] text-[13px] font-semibold text-[#4c1d95] whitespace-nowrap">
          {filtersOpen ? "Masquer les filtres" : "Filtres"}
          {pills.length ? ` (${pills.length})` : ""}
        </button>
      </div>

      {panel && (
        <div className="flex flex-wrap gap-x-[22px] gap-y-2.5 px-4 py-3.5 bg-white border border-[#ede9fe] rounded-xl mb-3">
          <div className="w-full text-[11px] font-bold tracking-[0.08em] text-[#8b81a8] uppercase">Colonnes affichées</div>
          {(
            [
              ["ticket", "Code ticket"],
              ["refs", "Références de paiement"],
              ["montant", "Montant et solde"],
              ["board", "Convoyage"],
              ["audit", "Historique du dossier"],
            ] as const
          ).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-[13px] text-[#3f3a52] cursor-pointer">
              <input type="checkbox" checked={cols[k]} onChange={() => setCols((c) => ({ ...c, [k]: !c[k] }))} className="w-[15px] h-[15px] accent-[#6d28d9]" />
              {label}
            </label>
          ))}
        </div>
      )}

      {filtersOpen && (
        <div className="p-4 bg-white border border-[#ede9fe] rounded-xl mb-3">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
            {filterFields.map((fl) => (
              <label key={fl.key} className="block">
                <span className={fieldLabelCls}>{fl.label}</span>
                <select value={f[fl.key]} onChange={(e) => setF(fl.key, e.target.value)} className={selectCls}>
                  {fl.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <label className="block">
              <span className={fieldLabelCls}>Voyage du</span>
              <input type="date" value={f.from} onChange={(e) => setF("from", e.target.value)} className={selectCls} />
            </label>
            <label className="block">
              <span className={fieldLabelCls}>Voyage au</span>
              <input type="date" value={f.to} onChange={(e) => setF("to", e.target.value)} className={selectCls} />
            </label>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-3.5 pt-3 border-t border-[#f1ecfe]">
            <span className="text-xs text-[#8b81a8] mr-0.5">Filtres combinés :</span>
            {pills.map((p) => (
              <span key={p.label} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 bg-[#f1ecfe] rounded-full text-xs font-semibold text-[#4c1d95] whitespace-nowrap">
                {p.label}
                <button type="button" onClick={p.clear} className="w-4 h-4 rounded-full bg-[#ddd0fb] text-[#4c1d95] text-[11px] leading-none">
                  ×
                </button>
              </span>
            ))}
            {pills.length === 0 && <span className="text-xs text-[#b6afc9]">aucun — tous les enregistrements</span>}
            <button type="button" onClick={() => setFState(EMPTY_F)} className="ml-auto h-8 px-3 border border-[#e2e8f0] bg-white rounded-lg text-xs font-semibold text-[#64748b]">
              Réinitialiser
            </button>
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ f, cols }));
                } catch {}
                flash("Vue filtrée enregistrée");
              }}
              className="h-8 px-3 bg-[#6d28d9] rounded-lg text-xs font-bold text-white"
            >
              Enregistrer la vue
            </button>
          </div>
        </div>
      )}

      {selIds.length > 0 && (
        <div className="flex items-center gap-2.5 flex-wrap px-4 py-3 bg-[#1f1b2e] rounded-xl mb-3">
          <span className="text-[13px] font-bold text-white">{selIds.length} réservation(s) sélectionnée(s)</span>
          <span className="w-px h-5 bg-[#4a4266]" />
          {bulkActions.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={() => {
                a.run();
                setSel({});
              }}
              className="h-8 px-3 border border-[#4a4266] bg-[#2c2643] hover:bg-[#3a3255] rounded-lg text-xs font-semibold text-[#e9e4ff] whitespace-nowrap"
            >
              {a.label}
            </button>
          ))}
          <button type="button" onClick={() => setSel({})} className="ml-auto text-xs font-semibold text-[#a79ec9] whitespace-nowrap">
            Désélectionner
          </button>
        </div>
      )}

      {/* List wrapper (can go full screen) */}
      <div className={classNames(fsList ? "fixed inset-0 z-[75] bg-[#faf7ff] overflow-auto" : "relative", fsList && (mob ? "p-3" : "px-[22px] py-[18px]"))}>
        <div className="flex items-center gap-2.5 flex-wrap mb-2.5">
          <div className="text-sm font-bold text-[#241f36]">Liste des réservations</div>
          <span className="px-2 py-0.5 bg-[#f1ecfe] rounded-full text-xs font-bold text-[#5b21b6] whitespace-nowrap">
            {rows.length} ligne{rows.length > 1 ? "s" : ""}
          </span>
          {fsList && (
            <>
              {tabs}
              <input value={f.q} onChange={(e) => setF("q", e.target.value)} placeholder="Rechercher dans la liste…" className="flex-1 min-w-[220px] h-[34px] px-3 border border-[#ddd6fe] rounded-[9px] bg-white text-[13px]" />
            </>
          )}
          <button
            type="button"
            onClick={() => setFsList(!fsList)}
            title="Afficher uniquement la liste en plein écran"
            className="ml-auto flex items-center gap-1.5 h-[34px] px-3 border rounded-[9px] text-[12.5px] font-semibold whitespace-nowrap"
            style={{ borderColor: fsList ? "#1f1b2e" : "#ddd6fe", background: fsList ? "#1f1b2e" : "#fff", color: fsList ? "#fff" : "#4c1d95" }}
          >
            <span className="text-[13px] leading-none">⛶</span>
            {fsList ? "Quitter le plein écran" : "Plein écran liste"}
          </button>
        </div>
        {view === "table" && !mob && tableView}
        {view === "kanban" && kanbanView}
        {(view === "grid" || (view === "table" && mob)) && gridView}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-[18px] flex-wrap mt-4 px-4 py-3 bg-white border border-[#ece7f8] rounded-xl">
        <span className="text-[11px] font-bold tracking-[0.07em] uppercase text-[#8b81a8]">Légende</span>
        {LEGEND.map((g) => (
          <span key={g.label} className="inline-flex items-center gap-1.5 text-xs text-[#4a4361]">
            <span className="w-[9px] h-[9px] rounded-full" style={{ background: g.color }} />
            {g.label}
          </span>
        ))}
      </div>

      {/* Quick actions: right rail on desktop, bottom bar on mobile */}
      {mob ? (
        <div className="fixed left-0 right-0 bottom-0 z-50 flex gap-1.5 overflow-x-auto px-2.5 pt-2 pb-[calc(8px+env(safe-area-inset-bottom))] bg-white border-t border-[#ece7f8] shadow-[0_-8px_20px_rgba(31,27,46,0.08)]">
          {fabs.map((b) => (
            <button key={b.label} type="button" onClick={b.run} title={b.title} className="shrink-0 w-[74px] min-h-[52px] px-1 py-1.5 flex flex-col items-center justify-center gap-0.5 rounded-[10px] text-[10.5px] font-bold leading-tight" style={{ background: b.bg, color: b.fg }}>
              <span className="text-[15px] leading-none">{b.glyph}</span>
              {b.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="fixed right-5 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-2.5">
          {fabs.map((b) => (
            <button key={b.label} type="button" onClick={b.run} title={b.title} className="w-20 min-h-[60px] px-1.5 py-2 flex flex-col items-center justify-center gap-1 rounded-[10px] text-[11px] font-bold leading-tight shadow-[0_6px_16px_rgba(31,27,46,0.14)] hover:brightness-95 whitespace-nowrap" style={{ background: b.bg, color: b.fg }}>
              <span className="text-[17px] leading-none font-medium">{b.glyph}</span>
              {b.label}
            </button>
          ))}
        </div>
      )}
      <button type="button" onClick={() => flash("WhatsApp Business ouvert")} title="WhatsApp" className="fixed left-[18px] z-50 w-[58px] h-[58px] rounded-full bg-[#22c55e] hover:bg-[#16a34a] text-white text-[13px] font-bold shadow-[0_10px_24px_rgba(34,197,94,0.4)]" style={{ bottom: mob ? 92 : 26 }}>
        WA
      </button>

      {/* Full-screen reservation */}
      {fsR && (
        <ReservationFullscreen
          r={fsR}
          list={list}
          index={fsIdx < 0 ? "hors filtre" : `${fsIdx + 1} / ${fsIds.length}`}
          onPrev={() => goFs(-1)}
          onNext={() => goFs(1)}
          onClose={() => setFsResa(null)}
          onOpenOther={setFsResa}
          actions={fsActions}
        />
      )}

      {wiz && (
        <ReservationWizardModal
          wiz={wiz}
          setWiz={setWiz}
          list={list}
          onClose={() => setWiz(null)}
          onCommit={(w) => {
            const res = applyWizard(list, seq, w, AGENT);
            setList(res.list);
            setSeq(res.seq);
            return res.ids;
          }}
          onShowTicket={setTk}
          onFlash={flash}
        />
      )}

      {bordOpen && (
        <BordereauModal
          list={list}
          onClose={() => setBordOpen(false)}
          onFlash={flash}
          onToggleBoard={(id, status) => upd(id, { board: status }, status === "Embarqué" ? "Embarqué (bordereau)" : "Embarquement annulé")}
          onOpenTripResa={() => {
            setBordOpen(false);
            openWiz({ ...newWiz("resa"), trip: "OPEN", step: 2 });
          }}
        />
      )}

      {tk && list.find((r) => r.id === tk) && <TicketModal reservation={list.find((r) => r.id === tk)!} onClose={() => setTk(null)} onFlash={flash} />}

      {payL && (
        <PaymentLinkModal
          rows={list.filter((r) => payL.includes(r.id))}
          onClose={() => setPayL(null)}
          onConfirm={() => {
            const n = payL.length;
            payL.forEach((id) => upd(id, { linkSent: true }, "Lien de paiement Mobile Money envoyé"));
            setPayL(null);
            flash(`${n} lien(s) de paiement envoyé(s)`);
          }}
        />
      )}

      {refM && list.find((r) => r.id === refM) && (
        <ModifRefModal
          reservation={list.find((r) => r.id === refM)!}
          onClose={() => setRefM(null)}
          onSave={(om, momo) => {
            const r = list.find((x) => x.id === refM)!;
            const has = om || momo;
            // A reference on an unpaid booking marks it paid; the ticket still needs confirming.
            upd(refM, { refOM: om, refMomo: momo, ...(has && r.pay === "Non payé" ? { pay: "Payé", paid: r.montant, canal: om ? "Orange Money" : "MTN MoMo" } : {}) }, "Référence de paiement mise à jour");
            setRefM(null);
            flash("Référence enregistrée — à confirmer");
          }}
        />
      )}

      {toast && (
        <div className="fixed left-1/2 bottom-[26px] -translate-x-1/2 z-[95] px-[18px] py-2.5 bg-[#1f1b2e] rounded-full text-[13px] font-semibold text-white shadow-[0_14px_34px_rgba(31,27,46,0.3)]">{toast}</div>
      )}
    </div>
  );
}

function HeaderBtn({ onClick, dot, label }: { onClick: () => void; dot: string; label: string }) {
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-1.5 h-[38px] px-3.5 border border-[#cbd5e1] bg-white hover:bg-[#f8fafc] hover:border-[#94a3b8] rounded-[9px] text-[13px] font-semibold text-[#334155] whitespace-nowrap">
      <span className="w-2 h-2 rounded-full" style={{ background: dot }} />
      {label}
    </button>
  );
}

function GridCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.06em] uppercase text-[#a79ec9] font-bold">{label}</div>
      <div className={classNames("text-[12.5px] text-[#3f3a52] mt-0.5", mono && "font-mono text-xs")}>{value}</div>
    </div>
  );
}
